<template>
  <div
    v-outside-click="{ handler: handleClickOutside }"
    :class="b()"
    :style="basketStyle"
  >
    <e-button
      :class="b('toggle', { isOpen })"
      @click="isOpen = !isOpen"
    >
      {{ $t('c-favorites-basket.toggle-label', { count: favoritesStore.favorites?.length ?? 0 }) }}
      <e-icon
        :class="b('arrow', { isOpen })"
        icon="i-arrow--down"
      />
    </e-button>

    <div
      v-if="isOpen"
      :class="b('wrapper')"
    >
      <p>
        {{ $t('c-favorites-basket.info') }}
      </p>

      <div
        v-if="favoritesStore.favorites?.length"
        :class="b('favorites')"
      >
        <a
          v-for="favorite in favoritesStore.favorites"
          :key="favorite.name"
          :href="favorite.url"
          :class="b('favorite')"
        >
          <e-picture
            v-if="favorite.mainImage"
            :srcset="favorite.mainImage.srcset"
            :alt="favorite.mainImage?.alt || favorite.mainImage?.title || $t('c-product-image.alt-text')"
            :title="favorite.mainImage.title"
            :fallback="favorite.mainImage.fallback"
            :ratio="1"
            :placeholder="!favorite.mainImage"
            loading="lazy"
            :class="b('image')"
          />
          <div :class="b('id-name-wrapper')">
            <div :class="b('sku')">{{ favorite.sku }}</div>
            <div>{{ favorite.name }}</div>
          </div>
          <e-button
            :class="b('delete-btn')"
            @click.prevent.stop="favoritesStore.removeFavorite(favorite)"
          >
            <e-icon
              :class="b('icon')"
              icon="i-close"
              size="16"
            />
          </e-button>
        </a>
      </div>

      <div :class="b('action-buttons')">
        <e-button
          :href="sessionStore.favoritesOverviewPageLink"
          variant="secondary"
          type="button"
          :class="b('action-btn')"
        >
          {{ $t('c-favorites-basket.see-all-favorites') }}
        </e-button>
        <e-button
          v-if="favoritesStore.favorites?.length"
          variant="secondary"
          :class="b('action-btn')"
          @click="onClearListClick"
        >
          {{ $t('c-favorites-basket.clear-list-label') }}
        </e-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { CSSProperties, defineComponent } from 'vue';
  import useFavoritesStore from '@/stores/favorites';
  import useSessionStore from '@/stores/session';
  import OutsideClickDirective from '@/directives/outside-click';
  import eButton from '@/elements/e-button.vue';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';

  type Setup = {
    favoritesStore: ReturnType<typeof useFavoritesStore>;
    sessionStore: ReturnType<typeof useSessionStore>;
  };

  type Data = {
    isOpen: boolean;
    basketStyle: Partial<CSSProperties>;
    scheduledFrameId: number | null;
    footerEl: HTMLElement | null;
  };

  /**
   * Renders the favorites basket component at the bottom of a page
   * but always above the footer
   */
  export default defineComponent({
    name: 'c-favorites-basket',
    components: { eButton, eIcon, ePicture },

    directives: {
      outsideClick: OutsideClickDirective.directive,
    },

    // props: {},
    // emits: {},
    setup(): Setup {
      return {
        favoritesStore: useFavoritesStore(),
        sessionStore: useSessionStore(),
      };
    },

    data(): Data {
      return {
        isOpen: false,
        basketStyle: { position: 'fixed', bottom: '0px', right: '0px', display: 'none' },
        scheduledFrameId: null,
        footerEl: null,
      };
    },

    // computed: {},

    // watch: {},
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},

    async mounted() {
      if (!this.favoritesStore.favorites) {
        await this.favoritesStore.apiFetchFavorites();
      }

      this.footerEl = document.querySelector('footer');
      this.scheduleUpdate();

      window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleUpdate);

      setTimeout(() => {
        this.updateBasketPosition();
        this.basketStyle.display = 'block';
      }, 1000);
    },

    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},

    beforeUnmount() {
      if (this.scheduledFrameId != null) {
        cancelAnimationFrame(this.scheduledFrameId);
      }

      window.removeEventListener('scroll', this.scheduleUpdate);
      window.removeEventListener('resize', this.scheduleUpdate);
    },

    methods: {
      updateBasketPosition(): void {
        const footer = this.footerEl ?? document.querySelector('footer');

        if (!footer) {
          this.basketStyle.bottom = '0px';

          return;
        }

        const overlap = window.innerHeight - footer.getBoundingClientRect().top;

        this.basketStyle.bottom = overlap > 0 ? `${Math.ceil(overlap)}px` : '0px';
      },

      scheduleUpdate(): void {
        if (this.scheduledFrameId != null) {
          cancelAnimationFrame(this.scheduledFrameId);
        }

        this.scheduledFrameId = requestAnimationFrame(() => this.updateBasketPosition());
      },

      handleClickOutside(): void {
        this.isOpen = false;
      },

      onClearListClick(): void {
        // eslint-disable-next-line
        if (window.confirm(this.$t('c-favorites-basket.clear-list-confirmation-message'))) {
          this.favoritesStore.clearList();
        }
      },
    },
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-favorites-basket {
    width: 100%;
    background-color: var(--theme-color-primary--1);
    color: var(--theme-color-grayscale--1000);
    pointer-events: auto;
    z-index: map.get(variables.$va-z-index, 'navigation') - 1;
    box-shadow: 0 0 10px rgba(255, 255, 255, 80%);

    @include mixins.media(sm) {
      width: 22rem;
    }

    &__toggle {
      width: 100%;
      padding: 0.75rem 1.5rem;
      color: var(--theme-color-grayscale--1000);
      font-weight: 700;

      &--is-open {
        border-bottom: 1px solid var(--theme-color-grayscale--1000);
      }

      .e-button__inner {
        display: flex;
        justify-content: space-between;
      }
    }

    &__arrow {
      width: 1.5rem;
      color: var(--theme-color-grayscale--1000);

      &--is-open {
        transform: rotate(180deg);
      }
    }

    &__wrapper {
      padding: 1.5rem;
    }

    &__favorites {
      @include mixins.hide-scrollbars;

      max-height: 20rem;
      overflow: scroll;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__clear-list-btn {
      color: var(--theme-color-grayscale--950);

      .e-button__inner {
        display: flex;
        gap: 0.5rem;
      }
    }

    &__favorite {
      @include mixins.hover-effect--default();

      position: relative;
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      border-bottom: 1px solid rgba(var(--theme-color-grayscale--950-rgb), 0.2);
      padding: 0.75rem 0;
    }

    &__delete-btn {
      color: var(--theme-color-grayscale--950);
    }

    &__image {
      min-width: 5rem;
      max-width: 5rem;
    }

    &__id-name-wrapper {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      flex-grow: 1;
    }

    &__action-buttons {
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__action-btn {
      width: 100%;
      display: flex;
      justify-content: center;
      color: var(--theme-color-primary--1);
      background-color: var(--theme-color-grayscale--950);
    }

    &__sku {
      font-size: variables.$font-size--smaller;
    }
  }
</style>
